/*****
    This Mixin handles form field components with manual chunking strategy:
    - Basic components: statically imported and bundled together
    - Advanced components: dynamically imported for separate chunks
*****/

import { markRaw } from "vue";
import { camelCase } from "lodash-es";

// Static imports - Basic components bundled in one chunk
import Header from "@/components/form_fields/basic/heading.vue";
import Paragraph from "@/components/form_fields/basic/paragraph.vue";
import ShortText from "@/components/form_fields/basic/short-text.vue";
import Name from "@/components/form_fields/basic/name.vue";
import Date from "@/components/form_fields/basic/date.vue";
import Time from "@/components/form_fields/basic/time.vue";
import LongText from "@/components/form_fields/basic/long-text.vue";
import Dropdown from "@/components/form_fields/basic/dropdown.vue";
import Address from "@/components/form_fields/basic/address.vue";
import Radio from "@/components/form_fields/basic/radio.vue";
import MultipleCheckbox from "@/components/form_fields/basic/multiple-checkbox.vue";
import Signature from "@/components/form_fields/basic/signature.vue";
import ScaleRating from "@/components/form_fields/basic/scale-rating.vue";
import StarRating from "@/components/form_fields/basic/star-rating.vue";
import Ranking from "@/components/form_fields/basic/ranking.vue";
import Spacer from "@/components/form_fields/basic/spacer.vue";
import FormVideo from "@/components/form_fields/basic/form-video.vue";
import FormAudio from "@/components/form_fields/basic/form-audio.vue";
import FormEmbed from "@/components/form_fields/basic/form-embed.vue";
import PictureCheckbox from "@/components/form_fields/basic/picture-choice.vue";
import Submit from "@/components/form_fields/basic/submit/submit.vue";
import ThankYou from "@/components/form_fields/basic/thank-you/thank-you.vue";

const importComponents = {
  data() {
    return {
      components: {
        // Basic components - statically imported (bundled together)
        header: markRaw(Header),
        paragraph: markRaw(Paragraph),
        shorttext: markRaw(ShortText),
        url: markRaw(ShortText),
        email: markRaw(ShortText),
        number: markRaw(ShortText),
        name: markRaw(Name),
        date: markRaw(Date),
        time: markRaw(Time),
        longtext: markRaw(LongText),
        dropdown: markRaw(Dropdown),
        address: markRaw(Address),
        radio: markRaw(Radio),
        "multiple-checkbox": markRaw(MultipleCheckbox),
        signature: markRaw(Signature),
        "scale-rating": markRaw(ScaleRating),
        "star-rating": markRaw(StarRating),
        ranking: markRaw(Ranking),
        spacer: markRaw(Spacer),
        "form-video": markRaw(FormVideo),
        "form-audio": markRaw(FormAudio),
        "form-embed": markRaw(FormEmbed),
        "picture-checkbox": markRaw(PictureCheckbox),
        submit: markRaw(Submit),
        "thank-you": markRaw(ThankYou),

        // Advanced components - will be dynamically imported (separate chunks)
        welcome: null,
        subheader: null,
        calculation: null,
        "column-layout": null,
        "auto-id": null,
        "repeat-field": null,
        "invisible-recaptcha": null,
        recaptcha: null,
        file: null,
        scheduler: null,
        "cf-turnstile": null,
        phone: null,
        image: null,
        location: null,
        matrix: null,
        paypal: null,
        stripe: null,
      },
    };
  },
  methods: {
    async importAllComponents() {
      // Basic components are already statically imported
      // Import ALL advanced components regardless of usage (for builder)
      const allAdvancedComponents = this.getAllAdvancedComponents();

      const importPromises = allAdvancedComponents.map(
        async (componentType) => {
          try {
            return this[`${camelCase(componentType)}Import`]();
          } catch (e) {
            console.error(`Error importing ${componentType}:`, e);
          }
        },
      );

      await Promise.all(importPromises);
    },

    async importUsedComponents() {
      // Basic components are already statically imported
      // Only import advanced components that are actually used in the form
      const advancedComponentsToImport = this.getUsedAdvancedComponents();

      const importPromises = advancedComponentsToImport.map(
        async (componentType) => {
          try {
            return this[`${camelCase(componentType)}Import`]();
          } catch (e) {
            console.error(`Error importing ${componentType}:`, e);
          }
        },
      );

      await Promise.all(importPromises);
    },

    getAllAdvancedComponents() {
      return [
        "welcome",
        "subheader",
        "calculation",
        "column-layout",
        "auto-id",
        "repeat-field",
        "invisible-recaptcha",
        "recaptcha",
        "file",
        "scheduler",
        "cf-turnstile",
        "phone",
        "image",
        "location",
        "matrix",
        "paypal",
        "stripe",
      ];
    },

    getUsedAdvancedComponents() {
      // Get the form elements that are being used
      const usedTypes = new Set();

      if (this.form) {
        this.form.forEach((el) => {
          if (this.isAdvancedComponent(el.type)) {
            usedTypes.add(el.type);
          }

          // Check nested components
          if (el.components) {
            el.components.forEach((comp) => {
              if (this.isAdvancedComponent(comp.type)) {
                usedTypes.add(comp.type);
              }
            });
          }
        });
      }

      // Always import invisible recaptcha if enabled
      if (this.styling?.addInvisibleRecaptcha) {
        usedTypes.add("invisible-recaptcha");
      }

      return Array.from(usedTypes);
    },

    isAdvancedComponent(type) {
      return this.getAllAdvancedComponents().includes(type);
    },

    // Dynamic import methods for advanced components
    async welcomeImport() {
      const module = await import(
        "@/components/form_fields/advanced/welcome.vue"
      );
      this.components["welcome"] = markRaw(module.default);
    },

    async subheaderImport() {
      const module = await import(
        "@/components/form_fields/advanced/sub-heading.vue"
      );
      this.components["subheader"] = markRaw(module.default);
    },

    async calculationImport() {
      const module = await import(
        "@/components/form_fields/advanced/calculation.vue"
      );
      this.components["calculation"] = markRaw(module.default);
    },

    async columnLayoutImport() {
      const module = await import(
        "@/components/form_fields/advanced/column-layout.vue"
      );
      this.components["column-layout"] = markRaw(module.default);
    },

    async autoIdImport() {
      const module = await import(
        "@/components/form_fields/advanced/auto-id.vue"
      );
      this.components["auto-id"] = markRaw(module.default);
    },

    async repeatFieldImport() {
      const module = await import(
        "@/components/form_fields/advanced/repeat-field.vue"
      );
      this.components["repeat-field"] = markRaw(module.default);
    },

    async invisibleRecaptchaImport() {
      const module = await import(
        "@/components/form_fields/advanced/recaptcha/invisible-recaptcha.vue"
      );
      this.components["invisible-recaptcha"] = markRaw(module.default);
    },

    async recaptchaImport() {
      const module = await import(
        "@/components/form_fields/advanced/recaptcha/recaptcha.vue"
      );
      this.components["recaptcha"] = markRaw(module.default);
    },

    async fileImport() {
      const module = await import("@/components/form_fields/advanced/file.vue");
      this.components["file"] = markRaw(module.default);
    },

    async schedulerImport() {
      const module = await import(
        "@/components/form_fields/advanced/scheduler.vue"
      );
      this.components["scheduler"] = markRaw(module.default);
    },

    async cfTurnstileImport() {
      const module = await import(
        "@/components/form_fields/advanced/recaptcha/turnstile.vue"
      );
      this.components["cf-turnstile"] = markRaw(module.default);
    },

    async phoneImport() {
      const module = await import(
        "@/components/form_fields/advanced/phone/phone.vue"
      );
      this.components["phone"] = markRaw(module.default);
    },

    async imageImport() {
      const module = await import(
        "@/components/form_fields/advanced/image/image.vue"
      );
      this.components["image"] = markRaw(module.default);
    },

    async locationImport() {
      const module = await import(
        "@/components/form_fields/advanced/location/location.vue"
      );
      this.components["location"] = markRaw(module.default);
    },

    async matrixImport() {
      const module = await import(
        "@/components/form_fields/advanced/matrix.vue"
      );
      this.components["matrix"] = markRaw(module.default);
    },

    async paypalImport() {
      const module = await import(
        "@/components/form_fields/advanced/payments/index.vue"
      );
      this.components["paypal"] = markRaw(module.default);
    },

    async stripeImport() {
      const module = await import(
        "@/components/form_fields/advanced/payments/index.vue"
      );
      this.components["stripe"] = markRaw(module.default);
    },
  },
};

export default importComponents;
